import Cta from "@components/Cta/Cta";
import { GeneralLink } from "@customTypes/generalLink";
import { useCallback } from "react";
import styles from "./C270_CtaContainer.module.scss";

interface CtaLink {
  fields: {
    description?: string;
    cta: GeneralLink;
    isSecondaryCta: boolean;
  };
}

interface Cta {
  description?: string;
  cta: GeneralLink;
  isSecondaryCta: boolean;
}

export interface CtaContainerLinkProps {
  ariaDescribedBy: string;
  content: Cta;
  isBlue: boolean;
  ctaContent: CtaLink;
}

function getID(href: string): string | null {
  const i = href.indexOf("#");
  return i === -1 ? null : href.slice(i + 1);
}

export const CtaContainerLink = (props: CtaContainerLinkProps) => {
  const { ariaDescribedBy, content, ctaContent, isBlue } = props;

  const ctaData = content || ctaContent?.fields;
  const url = ctaData?.cta?.fields?.url ?? "";

  const linkUrl =
    typeof window !== "undefined" ? new URL(url, window.location.href) : null;
  const samePath = linkUrl
    ? linkUrl.pathname === window.location.pathname
    : false;
  const isSearch = !!linkUrl?.search;
  const id = getID(url);
  const isSamePageAnchor = samePath && !isSearch && id;

  // Only handle click for same page anchor links without search params
  const handleClick = useCallback(
    (
      _:
        | React.MouseEvent<HTMLAnchorElement, MouseEvent>
        | React.KeyboardEvent<HTMLAnchorElement>
    ) => {
      const el = document.getElementById(id);
      if (!el) return;

      if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");

      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => el.focus({ preventScroll: true }), 250);

      if (history.pushState) {
        const { pathname, search } = window.location;
        history.pushState(null, "", `${pathname}${search}#${id}`);
      }
    },
    [id]
  );

  return (
    <div className={styles["cta-container__content"]}>
      {ctaData?.description && <p>{ctaData?.description}</p>}
      <div className={styles["cta-container__buttons"]}>
        <Cta
          {...ctaData?.cta?.fields}
          ariaDescribedBy={ariaDescribedBy}
          light={isBlue}
          type={ctaData?.isSecondaryCta ? "secondary" : "primary"}
          onClick={isSamePageAnchor ? handleClick : undefined}
        />
      </div>
    </div>
  );
};

export default CtaContainerLink;
